import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { handleApiCall } from "src/_config";
import { hasPermission } from "src/_common/utils/permissionsUtils";

// Import tab components
import MyActivityTab from "./tabs/MyActivityTab";
import AllUsersTab from "./tabs/AllUsersTab";

interface ActivityData {
  id: string;
  dateTime: string;
  user?: string;
  action: string;
  details: string;
  ipAddress: string;
}

interface HistoryMainProps { }

const HistoryMain: React.FC<HistoryMainProps> = () => {
  const history = useHistory();
  const userApi = useUserApi();
  const toast = useToaster();
  const userSelector = useAppUserDetailsSelector();
  const { t } = useTranslation("settings");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();

  const [activeTab, setActiveTab] = useState<'myActivity' | 'allUsers'>('myActivity');
  const [isLoading, setIsLoading] = useState(false);

  const [myActivityData, setMyActivityData] = useState<ActivityData[]>([
    {
      id: "1",
      dateTime: "Nov 5, 2025 – 10:32 AM",
      action: "Generated Offer Letter",
      details: "Candidate: John Miller",
      ipAddress: "192.168.1.12"
    },
    {
      id: "2",
      dateTime: "Nov 4, 2025 – 2:15 PM",
      action: "Updated Profile",
      details: "Changed contact email",
      ipAddress: "192.168.1.11"
    },
    {
      id: "3",
      dateTime: "Nov 3, 2025 – 6:48 PM",
      action: "Downloaded Report",
      details: "Monthly Offer Summary",
      ipAddress: "192.168.1.10"
    },
    {
      id: "4",
      dateTime: "Nov 2, 2025 – 11:22 AM",
      action: "Created New Job",
      details: "Senior Developer Position",
      ipAddress: "192.168.1.12"
    },
    {
      id: "5",
      dateTime: "Nov 1, 2025 – 4:18 PM",
      action: "Reviewed Applications",
      details: "Frontend Developer Role",
      ipAddress: "192.168.1.11"
    }
  ]);

  const [allUsersData, setAllUsersData] = useState<ActivityData[]>([
    {
      id: "1",
      dateTime: "Nov 5, 2025 – 10:32 AM",
      user: "John Miller",
      action: "Generated Offer Letter",
      details: "Candidate: Sarah Wilson",
      ipAddress: "192.168.1.15"
    },
    {
      id: "2",
      dateTime: "Nov 4, 2025 – 2:15 PM",
      user: "Sarah Wilson",
      action: "Updated Profile",
      details: "Changed contact email",
      ipAddress: "192.168.1.16"
    },
    {
      id: "3",
      dateTime: "Nov 3, 2025 – 6:48 PM",
      user: "Mike Johnson",
      action: "Downloaded Report",
      details: "Weekly Analytics Report",
      ipAddress: "192.168.1.17"
    },
    {
      id: "4",
      dateTime: "Nov 2, 2025 – 11:22 AM",
      user: "Emily Davis",
      action: "Created New Job",
      details: "UI/UX Designer Position",
      ipAddress: "192.168.1.18"
    },
    {
      id: "5",
      dateTime: "Nov 1, 2025 – 4:18 PM",
      user: "Alex Thompson",
      action: "Reviewed Applications",
      details: "Backend Developer Role",
      ipAddress: "192.168.1.19"
    }
  ]);

  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === 'member') {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadMyActivity = canAccessMenu('read_history_my_activity');
  console.log("canReadMyActivity:", canReadMyActivity);
  const canReadHistoryAllUsers = canAccessMenu('read_history_all_users');

  const handleBackClick = () => {
    history.goBack();
  };

  return (
    <div className="dashboard-main member-page-wrapper">
      {/* Header Section */}
      <div className="drjob-page-header">
        <div className="drjob-header-left">
          <button className="back-button d-none" onClick={handleBackClick}>
            {t("history.backButton")}
          </button>
          <h1 className="drjob-page-title">{t("history.title")}</h1>
        </div>
      </div>

      <div className="drjob-member-list">
        <div className="drjob-table-container">
          {/* Tab Navigation */}
          <div className="history-tabs">
            {canReadMyActivity && <button
              className={`history-tab ${activeTab === 'myActivity' ? 'active' : ''}`}
              onClick={() => setActiveTab('myActivity')}
            >
              {t("history.tabs.myActivity")}
            </button>
            }
            {canReadHistoryAllUsers && <button
              className={`history-tab ${activeTab === 'allUsers' ? 'active' : ''}`}
              onClick={() => setActiveTab('allUsers')}
            >
              {t("history.tabs.allUsers")}
            </button>
            }
          </div>

          {/* Tab Content */}
          {activeTab === 'myActivity' && <MyActivityTab />}
          {activeTab === 'allUsers' && <AllUsersTab />}
        </div>
      </div>
    </div>
  );
};

export default HistoryMain;